import React from 'react';
import {
  DollarSign,
  BarChart3,
  Users,
  CheckCircle2,
  AlertTriangle,
  Edit3,
  Layers,
  Download,
  Upload,
} from 'lucide-react';
import { calculateFinancials } from '../../core/financials.ts';
import { Category, EventItem, RuleConfig } from '../../core/types.ts';

interface FinancesPageProps {
  events: EventItem[];
  ruleConfig: RuleConfig;
  onEditEvent: (event: EventItem) => void;
  onExportJSON: () => void;
  onImportClick: () => void;
}

export const FinancesPage: React.FC<FinancesPageProps> = ({
  events,
  ruleConfig,
  onEditEvent,
  onExportJSON,
  onImportClick,
}) => {
  const financials = calculateFinancials(events, ruleConfig);

  return (
    <div className="main-container analysis-page">
      {/* Header Title & STIBET Directive Badge */}
      <div className="section-header" style={{ marginBottom: '1.5rem' }}>
        <div>
          <h2 className="section-title">
            <DollarSign size={24} color="var(--accent-primary)" />
            <span>DAAD STIBET &amp; Financial Board Dashboard</span>
          </h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            Official Financial Directive &amp; Expense Reimbursement Management (ESN TUMi Guidelines 2025/2026)
          </p>
        </div>
        <span className="brand-badge brand-badge-emerald">
          STIBET 10% Safety Buffer Active
        </span>
      </div>

      {/* Top KPI Ribbon */}
      <div className="stat-grid" style={{ marginBottom: '1.75rem' }}>
        <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
          <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#34d399' }}>
            <DollarSign size={24} strokeWidth={2} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total STIBET Funding Used</span>
            <span className="stat-value" style={{ color: '#10b981' }}>
              €{financials.totalStibetFundingUsed.toFixed(2)}
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              10% Buffer Reserved: €{financials.totalStibetBufferSavings.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #38bdf8' }}>
          <div className="stat-icon" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8' }}>
            <BarChart3 size={24} strokeWidth={2} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Event Expenses</span>
            <span className="stat-value" style={{ color: '#38bdf8' }}>
              €{financials.totalCostAtCap.toFixed(2)}
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Fixed: €{financials.totalFixedCosts.toFixed(0)} | Var: €{financials.totalVariableCostsAtCap.toFixed(0)}
            </span>
          </div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #c084fc' }}>
          <div className="stat-icon" style={{ background: 'rgba(192, 132, 252, 0.15)', color: '#c084fc' }}>
            <Users size={24} strokeWidth={2} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Participant Ticket Sales</span>
            <span className="stat-value" style={{ color: '#c084fc' }}>
              €{financials.totalParticipantRevenue.toFixed(2)}
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Net after Tax/App Fee: €{financials.totalNetRevenueAfterTax.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="stat-card" style={{ borderLeft: `4px solid ${financials.totalSectionNetBalance >= 0 ? '#10b981' : '#ef4444'}` }}>
          <div className="stat-icon" style={{ background: financials.totalSectionNetBalance >= 0 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)', color: financials.totalSectionNetBalance >= 0 ? '#34d399' : '#ef4444' }}>
            {financials.totalSectionNetBalance >= 0 ? <CheckCircle2 size={24} strokeWidth={2} /> : <AlertTriangle size={24} strokeWidth={2} />}
          </div>
          <div className="stat-content">
            <span className="stat-label">Section Net Balance</span>
            <span className="stat-value" style={{ color: financials.totalSectionNetBalance >= 0 ? '#10b981' : '#ef4444' }}>
              {financials.totalSectionNetBalance >= 0 ? `+€${financials.totalSectionNetBalance.toFixed(2)}` : `-€${Math.abs(financials.totalSectionNetBalance).toFixed(2)}`}
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {financials.totalSectionNetBalance >= 0 ? 'Balanced / Surplus' : 'Section Deficit'}
            </span>
          </div>
        </div>
      </div>

      {/* Funding Model Distribution Cards */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.85rem', color: 'var(--text-primary)' }}>
          Event Funding Models &amp; DAAD STIBET Breakdown
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.85rem' }}>
          <div className="config-card" style={{ padding: '1.15rem', borderTop: '4px solid #10b981' }}>
            <span className="brand-badge brand-badge-emerald">STIBET Free</span>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, marginTop: '0.5rem', color: 'var(--text-primary)' }}>
              {financials.eventsByFundingModel['stibet-free'] || 0} events
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              STIBET: €{(financials.stibetByFundingModel['stibet-free'] || 0).toFixed(0)}
            </div>
          </div>

          <div className="config-card" style={{ padding: '1.15rem', borderTop: '4px solid #38bdf8' }}>
            <span className="brand-badge brand-badge-sky">STIBET Partial</span>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, marginTop: '0.5rem', color: 'var(--text-primary)' }}>
              {financials.eventsByFundingModel['stibet-partial'] || 0} events
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              STIBET: €{(financials.stibetByFundingModel['stibet-partial'] || 0).toFixed(0)}
            </div>
          </div>

          <div className="config-card" style={{ padding: '1.15rem', borderTop: '4px solid #a855f7' }}>
            <span className="brand-badge brand-badge-purple">Fully Free</span>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, marginTop: '0.5rem', color: 'var(--text-primary)' }}>
              {financials.eventsByFundingModel['fully-free'] || 0} events
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Ticket Price: €0
            </div>
          </div>

          <div className="config-card" style={{ padding: '1.15rem', borderTop: '4px solid #f59e0b' }}>
            <span className="brand-badge brand-badge-amber">External</span>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, marginTop: '0.5rem', color: 'var(--text-primary)' }}>
              {financials.eventsByFundingModel['external'] || 0} events
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              External €{financials.totalExternalFunding.toFixed(0)}
            </div>
          </div>

          <div className="config-card" style={{ padding: '1.15rem', borderTop: '4px solid #ec4899' }}>
            <span className="brand-badge brand-badge-pink">Self-Funded</span>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, marginTop: '0.5rem', color: 'var(--text-primary)' }}>
              {financials.eventsByFundingModel['self-funded'] || 0} events
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              100% Ticket Sales
            </div>
          </div>
        </div>
      </div>

      {/* Complete Per-Event Financial & STIBET Audit Table */}
      <div className="analysis-section" style={{ marginBottom: '2rem' }}>
        <div className="section-header" style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            Complete Event STIBET &amp; Pricing Audit Table
          </h3>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Event Title</th>
                <th>Category</th>
                <th>Funding Model</th>
                <th>Capacity + Orgs</th>
                <th>Total Cost</th>
                <th>Non-Fundable</th>
                <th>STIBET Grant (10% Buffer)</th>
                <th>Ticket Price</th>
                <th>Tax Tier</th>
                <th>Section Net Balance</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {events.map(ev => {
                const detail = financials.eventDetails[ev.id];
                if (!detail) return null;

                return (
                  <tr key={ev.id}>
                    <td>
                      <strong style={{ color: 'var(--text-primary)' }}>{ev.title}</strong>
                    </td>
                    <td>
                      <span className="cat-pill" style={{ padding: '0.15rem 0.45rem', fontSize: '0.75rem', borderLeft: `3px solid var(--cat-${ev.category.toLowerCase()})` }}>
                        {ev.category}
                      </span>
                    </td>
                    <td>
                      <span className={`brand-badge ${detail.appliedStibetFunding > 0 ? 'brand-badge-emerald' : 'brand-badge-muted'}`}>
                        {detail.fundingModel} ({detail.stibetType})
                      </span>
                    </td>
                    <td>{detail.capacity} + {detail.organisers} ({detail.totalEligibleParticipants})</td>
                    <td>€{detail.totalCost.toFixed(2)}</td>
                    <td style={{ color: detail.nonFundableCost > 0 ? '#f59e0b' : 'var(--text-muted)' }}>
                      {detail.nonFundableCost > 0 ? `€${detail.nonFundableCost.toFixed(2)}` : '€0'}
                    </td>
                    <td style={{ fontWeight: 700, color: detail.appliedStibetFunding > 0 ? '#10b981' : 'var(--text-muted)' }}>
                      {detail.appliedStibetFunding > 0 ? `€${detail.appliedStibetFunding.toFixed(2)}` : '€0'}
                    </td>
                    <td>
                      {detail.actualPricePerParticipant === 0 ? (
                        <span style={{ color: '#38bdf8', fontWeight: 600 }}>FREE</span>
                      ) : (
                        <span>€{detail.actualPricePerParticipant.toFixed(2)} <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>(min €{detail.recommendedMinPricePerParticipant.toFixed(2)})</span></span>
                      )}
                    </td>
                    <td>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {detail.taxTier === 'cultural' ? '7% Tax' : detail.taxTier === 'alcohol' ? '19% Tax' : '0% Tax'}
                      </span>
                    </td>
                    <td style={{ fontWeight: 700, color: detail.sectionNetBalance >= 0 ? '#10b981' : '#ef4444' }}>
                      {detail.sectionNetBalance >= 0 ? `+€${detail.sectionNetBalance.toFixed(2)}` : `-€${Math.abs(detail.sectionNetBalance).toFixed(2)}`}
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => onEditEvent(ev)}
                        style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
                      >
                        <Edit3 size={12} />
                        <span>Edit</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: 'rgba(255,255,255,0.04)', fontWeight: 700 }}>
                <td>TOTALS ({financials.totalEvents} events)</td>
                <td>—</td>
                <td>—</td>
                <td>{financials.totalCapacity} spots</td>
                <td>€{financials.totalCostAtCap.toFixed(2)}</td>
                <td>€{financials.totalNonFundableCosts.toFixed(2)}</td>
                <td style={{ color: '#10b981' }}>€{financials.totalStibetFundingUsed.toFixed(2)}</td>
                <td>€{financials.totalParticipantRevenue.toFixed(2)} gross</td>
                <td>—</td>
                <td style={{ color: financials.totalSectionNetBalance >= 0 ? '#10b981' : '#ef4444' }}>
                  €{financials.totalSectionNetBalance.toFixed(2)}
                </td>
                <td>—</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Data Management & Export Section */}
      <div className="analysis-section">
        <div className="section-header" style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            Data Backup &amp; Financial Export
          </h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div className="json-dropzone" onClick={onExportJSON}>
            <Download size={32} color="var(--accent-primary)" style={{ marginBottom: '0.75rem' }} />
            <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Export Schedule &amp; Financials to JSON</h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Download complete state (events, pricing, STIBET models, capacity) as JSON.
            </p>
          </div>

          <div className="json-dropzone" onClick={onImportClick}>
            <Upload size={32} color="var(--accent-primary)" style={{ marginBottom: '0.75rem' }} />
            <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Import Schedule &amp; Financials from JSON</h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Restore schedule and financial data from a JSON backup.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
